/**
 * Common Validation Schemas
 *
 * Reusable Zod schemas shared across multiple forms in the application.
 * Provides consistent validation rules and error messages.
 *
 * Created: 2025-11-02
 * Version: 1.0
 */

import { z } from 'zod';

/* ============================================================================
   EMAIL VALIDATION
   ============================================================================ */

/**
 * Email Schema - Standard Email Validation
 *
 * Features:
 * - Valid email format check
 * - Automatic lowercase transformation
 * - Trimmed whitespace
 *
 * Usage:
 * ```ts
 * const schema = z.object({ email: emailSchema });
 * ```
 */
export const emailSchema = z
  .string()
  .min(1, 'Adresa de email este obligatorie')
  .email('Adresa de email nu este validă')
  .toLowerCase()
  .transform((val) => val.trim());

/* ============================================================================
   PASSWORD VALIDATION
   ============================================================================ */

/**
 * Password Schema - Basic Password Requirements
 *
 * Requirements:
 * - Minimum 6 characters
 * - Maximum 100 characters
 *
 * Usage:
 * ```ts
 * const schema = z.object({ parola: passwordSchema });
 * ```
 */
export const passwordSchema = z
  .string()
  .min(6, 'Parola trebuie să aibă minim 6 caractere')
  .max(100, 'Parola este prea lungă');

/**
 * Password Confirmation Schema - Password Matching Validation
 *
 * Validates that both password fields match.
 * Use .extend() to add additional fields to this schema.
 *
 * Usage:
 * ```ts
 * const loginSchema = z.object({
 *   email: emailSchema,
 * }).merge(passwordConfirmationSchema);
 * ```
 */
export const passwordConfirmationSchema = z
  .object({
    parola: passwordSchema,
    confirmaParola: z.string(),
  })
  .refine((data) => data.parola === data.confirmaParola, {
    message: 'Parolele nu coincid',
    path: ['confirmaParola'],
  });

/* ============================================================================
   PHONE VALIDATION
   ============================================================================ */

/**
 * Phone Schema - Romanian Phone Number Validation
 *
 * Accepts:
 * - Numbers (0-9)
 * - Plus sign (+)
 * - Spaces, hyphens, parentheses for formatting
 *
 * Requirements:
 * - Minimum 10 characters (Romanian phone format)
 *
 * Usage:
 * ```ts
 * const schema = z.object({ telefon: phoneSchema });
 * ```
 */
export const phoneSchema = z
  .string()
  .min(1, 'Numărul de telefon este obligatoriu')
  .regex(/^[0-9+\s()-]+$/, 'Număr de telefon invalid')
  .min(10, 'Număr de telefon prea scurt');

/* ============================================================================
   ADDRESS VALIDATION
   ============================================================================ */

/**
 * Address Schema - Romanian Address Format
 *
 * Validates complete Romanian address with all standard fields.
 * All fields optional except county (judet) and city (oras).
 *
 * Fields:
 * - id: Address ID (optional, auto-generated)
 * - judet: County (required)
 * - oras: City/Town (required)
 * - comuna: Municipality (optional)
 * - sat: Village (optional)
 * - strada: Street (optional)
 * - numar: Street number (optional)
 * - codPostal: Postal code (optional)
 * - bloc: Building block (optional)
 * - scara: Building entrance (optional)
 * - etaj: Floor (optional)
 * - apartament: Apartment number (optional)
 *
 * Usage:
 * ```ts
 * const schema = z.object({
 *   schoolAddress: addressSchema,
 *   deliveryAddress: addressSchema.optional(),
 * });
 * ```
 */
export const addressSchema = z.object({
  id: z.string().optional(),
  judet: z.string().min(1, 'Selectați județul'),
  oras: z.string().min(1, 'Introduceți orașul'),
  comuna: z.string().optional(),
  sat: z.string().optional(),
  strada: z.string().optional(),
  numar: z.string().optional(),
  codPostal: z.string().optional(),
  bloc: z.string().optional(),
  scara: z.string().optional(),
  etaj: z.string().optional(),
  apartament: z.string().optional(),
});

/**
 * Address Type (TypeScript)
 *
 * Inferred TypeScript type from addressSchema.
 * Use this type for function parameters and return types.
 */
export type Address = z.infer<typeof addressSchema>;

/* ============================================================================
   TEXT VALIDATION
   ============================================================================ */

/**
 * Name Schema - Person Name Validation
 *
 * For full names (first + last name).
 * Requirements: 3-100 characters
 *
 * Usage:
 * ```ts
 * const schema = z.object({ nume: nameSchema });
 * ```
 */
export const nameSchema = z
  .string()
  .min(3, 'Numele trebuie să aibă minim 3 caractere')
  .max(100, 'Numele este prea lung');

/**
 * Nickname Schema - Username/Nickname Validation
 *
 * For student nicknames and usernames.
 * Requirements: 3-50 characters
 *
 * Usage:
 * ```ts
 * const schema = z.object({ nickname: nicknameSchema });
 * ```
 */
export const nicknameSchema = z
  .string()
  .min(3, 'Nickname-ul trebuie să aibă minim 3 caractere')
  .max(50, 'Nickname-ul este prea lung');

/**
 * Required Text Schema - Generic Required Text Field
 *
 * For any text field that cannot be empty.
 * Custom error message can be provided.
 *
 * Usage:
 * ```ts
 * const schema = z.object({
 *   codProfesor: requiredText('Codul profesorului este obligatoriu'),
 * });
 * ```
 */
export const requiredText = (message = 'Acest câmp este obligatoriu') =>
  z.string().min(1, message);

/* ============================================================================
   NUMERIC VALIDATION
   ============================================================================ */

/**
 * Positive Number Schema - For Quantities and Weights
 *
 * Validates positive numbers (>= 0).
 * Useful for waste quantities, points, etc.
 *
 * Usage:
 * ```ts
 * const schema = z.object({
 *   kgDeee: positiveNumber('Cantitatea trebuie să fie pozitivă'),
 * });
 * ```
 */
export const positiveNumber = (message = 'Valoarea trebuie să fie pozitivă') =>
  z.number().min(0, message);

/**
 * CUI Schema - Romanian Company Identification Number
 *
 * Validates Romanian CUI (Cod Unic de Înregistrare).
 * Requirements: 2-10 digits
 *
 * Usage:
 * ```ts
 * const schema = z.object({ cui: cuiSchema });
 * ```
 */
export const cuiSchema = z
  .string()
  .min(1, 'CUI este obligatoriu')
  .regex(/^[0-9]+$/, 'CUI trebuie să conțină doar cifre')
  .min(2, 'CUI invalid')
  .max(10, 'CUI invalid');

/* ============================================================================
   BOOLEAN VALIDATION
   ============================================================================ */

/**
 * Required Boolean Schema - Checkbox that Must Be Checked
 *
 * For terms acceptance, consent checkboxes.
 * Validates that checkbox is checked (true).
 *
 * Usage:
 * ```ts
 * const schema = z.object({
 *   terms: requiredBoolean('Trebuie să acceptați termenii'),
 *   consent: requiredBoolean('Acordul este obligatoriu'),
 * });
 * ```
 */
export const requiredBoolean = (message = 'Acest câmp este obligatoriu') =>
  z.boolean().refine((val) => val === true, { message });

/* ============================================================================
   FILE VALIDATION
   ============================================================================ */

/**
 * File URL Schema - Single File URL Validation
 *
 * Validates a single file URL string (uploaded to storage).
 * Optional by default.
 *
 * Usage:
 * ```ts
 * const schema = z.object({
 *   document: fileUrlSchema,
 *   requiredDoc: fileUrlSchema.refine(val => !!val, 'Required'),
 * });
 * ```
 */
export const fileUrlSchema = z.string().url().optional();

/**
 * File Array Schema - Generic File Array Validation
 *
 * Validates an array of file URLs with configurable min/max bounds.
 * Useful for any file type (images, documents, etc.).
 *
 * Parameters:
 * - min: Minimum number of files (default: 0)
 * - max: Maximum number of files (default: 10)
 *
 * Usage:
 * ```ts
 * const schema = z.object({
 *   attachments: fileArraySchema(1, 5), // 1-5 files required
 *   optional: fileArraySchema(),         // 0-10 files optional
 * });
 * ```
 */
export const fileArraySchema = (min = 0, max = 10) =>
  z
    .array(z.string().url())
    .min(min, min > 0 ? `Minim ${min} fișiere sunt necesare` : undefined)
    .max(max, `Maxim ${max} fișiere permise`)
    .optional();

/**
 * Photo Array Schema - Image File Array Validation
 *
 * Validates an array of image URLs (JPG, JPEG, PNG, WEBP).
 * Extends fileArraySchema with image format validation.
 *
 * Parameters:
 * - min: Minimum number of photos (default: 2)
 * - max: Maximum number of photos (default: 10)
 *
 * Usage:
 * ```ts
 * const schema = z.object({
 *   wastePhotos: photoArraySchema(2, 10),  // 2-10 photos required
 *   optional: photoArraySchema(0, 5),      // 0-5 photos optional
 * });
 * ```
 */
export const photoArraySchema = (min = 2, max = 10) => {
  const baseSchema = z
    .array(z.string().url())
    .min(min, min > 0 ? `Minim ${min} fotografii sunt necesare` : undefined)
    .max(max, `Maxim ${max} fotografii permise`)
    .refine(
      (urls) => {
        if (!urls || urls.length === 0) return min === 0;
        return urls.every((url) => /\.(jpg|jpeg|png|webp)$/i.test(url));
      },
      { message: 'Doar imagini sunt permise (JPG, PNG, WEBP)' }
    );

  return min === 0 ? baseSchema.optional() : baseSchema;
};

/**
 * Document Array Schema - Document File Array Validation
 *
 * Validates an array of document URLs (PDF, DOC, DOCX).
 * Extends fileArraySchema with document format validation.
 *
 * Parameters:
 * - min: Minimum number of documents (default: 0)
 * - max: Maximum number of documents (default: 5)
 *
 * Usage:
 * ```ts
 * const schema = z.object({
 *   contracts: documentArraySchema(1, 3),  // 1-3 docs required
 *   optional: documentArraySchema(),        // 0-5 docs optional
 * });
 * ```
 */
export const documentArraySchema = (min = 0, max = 5) => {
  const baseSchema = z
    .array(z.string().url())
    .min(min, min > 0 ? `Minim ${min} documente sunt necesare` : undefined)
    .max(max, `Maxim ${max} documente permise`)
    .refine(
      (urls) => {
        if (!urls || urls.length === 0) return min === 0;
        return urls.every((url) => /\.(pdf|doc|docx)$/i.test(url));
      },
      { message: 'Doar documente sunt permise (PDF, DOC, DOCX)' }
    );

  return min === 0 ? baseSchema.optional() : baseSchema;
};

/* ============================================================================
   DATE VALIDATION
   ============================================================================ */

/**
 * Future Date Schema - Date Must Be In Future
 *
 * Validates that a date is at least N days from today.
 * Useful for pickup dates, event dates, etc.
 *
 * Parameters:
 * - minDaysFromNow: Minimum days from today (e.g., 15)
 * - message: Custom error message (optional)
 *
 * Usage:
 * ```ts
 * const schema = z.object({
 *   pickupDate: futureDateSchema(15), // Must be 15+ days from today
 *   eventDate: futureDateSchema(7, 'Evenimentul trebuie programat cu minim 7 zile înainte'),
 * });
 * ```
 */
export const futureDateSchema = (minDaysFromNow: number, message?: string) =>
  z.date().refine(
    (date) => {
      const minDate = new Date();
      minDate.setDate(minDate.getDate() + minDaysFromNow);
      minDate.setHours(0, 0, 0, 0);
      const inputDate = new Date(date);
      inputDate.setHours(0, 0, 0, 0);
      return inputDate >= minDate;
    },
    {
      message:
        message || `Data trebuie să fie minim ${minDaysFromNow} zile de la data curentă`,
    }
  );

/**
 * Past Date Schema - Date Must Be In Past
 *
 * Validates that a date is in the past (before today).
 * Optionally limits how far in the past.
 *
 * Parameters:
 * - maxDaysAgo: Maximum days in the past (optional, undefined = unlimited)
 * - message: Custom error message (optional)
 *
 * Usage:
 * ```ts
 * const schema = z.object({
 *   birthDate: pastDateSchema(),           // Any past date
 *   recentDate: pastDateSchema(30),        // Within last 30 days
 * });
 * ```
 */
export const pastDateSchema = (maxDaysAgo?: number, message?: string) =>
  z.date().refine(
    (date) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const inputDate = new Date(date);
      inputDate.setHours(0, 0, 0, 0);

      if (inputDate >= today) return false;

      if (maxDaysAgo !== undefined) {
        const minDate = new Date();
        minDate.setDate(minDate.getDate() - maxDaysAgo);
        minDate.setHours(0, 0, 0, 0);
        return inputDate >= minDate;
      }

      return true;
    },
    {
      message:
        message ||
        (maxDaysAgo
          ? `Data trebuie să fie în trecut (maxim ${maxDaysAgo} zile)`
          : 'Data trebuie să fie în trecut'),
    }
  );

/**
 * Date Range Schema - Date Must Be Within Range
 *
 * Validates that a date falls between two dates (inclusive).
 *
 * Parameters:
 * - minDate: Earliest allowed date
 * - maxDate: Latest allowed date
 * - message: Custom error message (optional)
 *
 * Usage:
 * ```ts
 * const schema = z.object({
 *   registrationDate: dateRangeSchema(
 *     new Date('2025-01-01'),
 *     new Date('2025-12-31'),
 *     'Data trebuie să fie în anul 2025'
 *   ),
 * });
 * ```
 */
export const dateRangeSchema = (minDate: Date, maxDate: Date, message?: string) =>
  z.date().refine(
    (date) => {
      const inputDate = new Date(date);
      inputDate.setHours(0, 0, 0, 0);
      const min = new Date(minDate);
      min.setHours(0, 0, 0, 0);
      const max = new Date(maxDate);
      max.setHours(0, 0, 0, 0);
      return inputDate >= min && inputDate <= max;
    },
    {
      message:
        message ||
        `Data trebuie să fie între ${minDate.toLocaleDateString('ro-RO')} și ${maxDate.toLocaleDateString('ro-RO')}`,
    }
  );

/* ============================================================================
   CONDITIONAL VALIDATION
   ============================================================================ */

/**
 * Tri-State Boolean Schema - Null/True/False Validation
 *
 * Validates a boolean that starts as null (unanswered) and must be set to true or false.
 * Useful for confirmation dialogs, yes/no questions that require explicit answer.
 *
 * Parameters:
 * - requiredMessage: Error message when still null (optional)
 *
 * Usage:
 * ```ts
 * const schema = z.object({
 *   confirmParticipation: triStateBoolean('Trebuie să confirmați participarea'),
 *   agreeToTerms: triStateBoolean(),
 * });
 * ```
 */
export const triStateBoolean = (requiredMessage?: string) =>
  z
    .boolean()
    .nullable()
    .refine((val) => val !== null, {
      message: requiredMessage || 'Trebuie să alegeți o opțiune',
    });

/**
 * At Least One True - Boolean Fields Validation
 *
 * Validates that at least one boolean field is true.
 * Useful for multi-select options where at least one is required.
 *
 * Parameters:
 * - fields: Array of field names to check
 *
 * Usage:
 * ```ts
 * const schema = z.object({
 *   sendToTeachers: z.boolean(),
 *   sendToStudents: z.boolean(),
 * }).refine(
 *   atLeastOneTrue(['sendToTeachers', 'sendToStudents']),
 *   { message: 'Selectați cel puțin un destinatar' }
 * );
 * ```
 */
export function atLeastOneTrue<T extends Record<string, any>>(
  fields: (keyof T)[]
): (data: T) => boolean {
  return (data: T) => fields.some((field) => data[field] === true);
}

/**
 * At Least One Greater Than Zero - Numeric Fields Validation
 *
 * Validates that at least one numeric field is greater than zero.
 * Useful for quantity forms where at least one item is required.
 *
 * Parameters:
 * - fields: Array of field names to check
 *
 * Usage:
 * ```ts
 * const schema = z.object({
 *   plasticKg: z.number(),
 *   paperKg: z.number(),
 *   metalKg: z.number(),
 * }).refine(
 *   atLeastOneGreaterThanZero(['plasticKg', 'paperKg', 'metalKg']),
 *   { message: 'Introduceți cel puțin o cantitate' }
 * );
 * ```
 */
export function atLeastOneGreaterThanZero<T extends Record<string, any>>(
  fields: (keyof T)[]
): (data: T) => boolean {
  return (data: T) => fields.some((field) => (data[field] || 0) > 0);
}

/* ============================================================================
   EXAMPLE USAGE
   ============================================================================ */

/**
 * Example: Registration Form Schema
 *
 * Demonstrates how to combine common schemas.
 *
 * ```ts
 * import { emailSchema, passwordConfirmationSchema, nameSchema } from '@/shared/validation/common.schemas';
 *
 * const registrationSchema = z.object({
 *   email: emailSchema,
 *   nume: nameSchema,
 * }).merge(passwordConfirmationSchema);
 *
 * type RegistrationData = z.infer<typeof registrationSchema>;
 * ```
 */

/**
 * Example: School Details Schema
 *
 * Demonstrates nested address schema usage.
 *
 * ```ts
 * import { addressSchema, phoneSchema, emailSchema } from '@/shared/validation/common.schemas';
 *
 * const schoolSchema = z.object({
 *   telefon: phoneSchema,
 *   email: emailSchema,
 *   schoolAddress: addressSchema,
 *   deliveryAddress: addressSchema.optional(),
 *   sameAsSchoolAddress: z.boolean(),
 * });
 * ```
 */
